/**
 * Pure-JS grouping helpers for the Swapcard schedule app.
 *
 * Extracted from ScheduleApp.vue so other views (e.g. SchedulePrintView.vue)
 * can share the exact same grouping logic without pulling in Vuex.
 */

const NON_SESSION_TYPES = new Set([
  'networking', 'meal', 'badge-pickup', 'badge pickup',
  'exhibits', 'coffee break', 'break', 'headshot', 'headshots',
  'speaker ready room',
]);

const NON_SESSION_ICON_MAP = {
  networking:           '/img/icons/schedule/md/networking.svg',
  meal:                 '/img/icons/schedule/md/lunch.svg',
  'badge-pickup':       '/img/icons/schedule/md/badge-pickup.svg',
  'badge pickup':       '/img/icons/schedule/md/badge-pickup.svg',
  exhibits:             '/img/icons/schedule/md/exhibit.svg',
  'coffee break':       '/img/icons/schedule/md/breakfast.svg',
  break:                '/img/icons/schedule/md/break.svg',
  headshot:             '/img/icons/schedule/md/camera.svg',
  headshots:             '/img/icons/schedule/md/camera.svg',
  'speaker ready room': '/img/icons/schedule/md/speaker-room.svg',
};

const NON_SESSION_FALLBACK_ICON = '/img/icons/schedule/md/calendar.svg';

/**
 * Returns true when the session type string identifies a non-session agenda
 * item (meal, break, or similar divider). Case-insensitive.
 */
export function isNonSessionItem(sessionType) {
  return NON_SESSION_TYPES.has((sessionType || '').toLowerCase());
}

/**
 * Returns the /img/icons/schedule/md/ path for the given session type,
 * falling back to calendar.svg for unrecognised types.
 */
export function getNonSessionIconPath(sessionType) {
  return NON_SESSION_ICON_MAP[(sessionType || '').toLowerCase()] || NON_SESSION_FALLBACK_ICON;
}

/**
 * Format an ISO date string (YYYY-MM-DD) as a human-readable day label.
 * Matches the DayTabs component's formatDayLabel display format.
 *
 * Uses UTC-safe date construction to ensure the displayed date always matches
 * the date portion of the input string, regardless of the browser's local timezone.
 *
 * @param {string} dateString — ISO date string, e.g. "2026-06-05"
 * @returns {string}
 */
export function formatDayLabel(dateString) {
  if (!dateString) return '';
  const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, y, m, d] = match;
  const utc = Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(utc);
}

/**
 * Short variant of formatDayLabel: produces "Wed, Sep 10".
 * Used by DayTabs in compact (5–6 days) and overflow (7+ days) modes.
 *
 * @param {string} dateString — ISO date string, e.g. "2026-06-05"
 * @returns {string}
 */
export function formatDayLabelShort(dateString) {
  if (!dateString) return '';
  const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const [, y, m, d] = match;
  const utc = Date.UTC(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(utc);
}

/**
 * Build a flat render list from a groups array, optionally injecting day
 * separator items between calendar-day boundaries.
 *
 * Separator items: { type: 'separator', dayLabel: string, dateValue: string }
 * Group items:     { type: 'group', id, startTime, sessions }
 *
 * Separators are only injected when isAllDays AND isTimeSort are both true.
 * Groups with an empty startTime are skipped for separator injection.
 *
 * @param {Array<{ id: string, startTime: string, sessions: Array, isNonSessionItem: boolean }>} groups
 * @param {boolean} isAllDays
 * @param {boolean} isTimeSort
 * @returns {Array}
 */
export function buildRenderList(groups, isAllDays, isTimeSort) {
  const items = [];
  let lastDateValue = null;

  for (const group of groups) {
    const dateValue = group.startTime ? group.startTime.slice(0, 10) : '';

    if (isAllDays && isTimeSort && dateValue && dateValue !== lastDateValue) {
      items.push({
        type: 'separator',
        dayLabel: formatDayLabel(dateValue),
        dateValue,
      });
      lastDateValue = dateValue;
    }

    items.push({ type: 'group', ...group });
  }

  return items;
}

/**
 * Decide whether the current sort/search state should group results by
 * start time or render them flat.
 *
 * Rule (matches the `isSortByTime` check in groupByStartTime):
 *  - Group by time when the explicit time sort is active.
 *  - Also group by time when the relevance sort is active AND no search
 *    term was used (the backend fulfils relevance+no-term with
 *    event_start_date order anyway, so grouping is meaningful).
 *  - Alphabetical and date sorts suppress grouping regardless of search term.
 *
 * @param {Object} args
 * @param {{ fieldName?: string } | null | undefined} args.sortOption
 * @param {string | null | undefined} args.searchTerm
 * @returns {'time-grouped' | 'flat'}
 */
export function pickGroupingMode({ sortOption, searchTerm } = {}) {
  const hasSearchTerm = !!(searchTerm || '').trim();
  const isTimeSort = sortOption && sortOption.fieldName === 'startTime';
  const isRelevanceWithNoTerm =
    !hasSearchTerm &&
    (!sortOption ||
      sortOption.fieldName === 'search.score()' ||
      sortOption.fieldName === 'score');
  const isSortByTime = isTimeSort || isRelevanceWithNoTerm;
  return isSortByTime ? 'time-grouped' : 'flat';
}

/**
 * Group session results by their `meta.startDate` when an explicit time
 * sort is active, or when the relevance sort is selected with no search
 * term. Alphabetical and date sorts suppress grouping regardless of
 * search term.
 *
 * In 'flat' mode, each session is returned as its own group of one so
 * downstream consumers can use a uniform shape.
 *
 * In 'time-grouped' mode, groups are sorted chronologically by
 * `startTime`; groups with an empty `startTime` sort first (preserving
 * the original ScheduleApp.vue behaviour).
 *
 * @param {Array<{ meta?: { startDate?: string } }>} results
 * @param {Object} args
 * @param {{ fieldName?: string } | null | undefined} args.sortOption
 * @param {string | null | undefined} args.searchTerm
 * @returns {Array<{ id: string, startTime: string, sessions: Array<Object>, isNonSessionItem: boolean }>}
 */
export function groupByStartTime(results, { sortOption, searchTerm } = {}) {
  const mode = pickGroupingMode({ sortOption, searchTerm });

  if (mode !== 'time-grouped') {
    return results.map((r, index) => ({
      id: r.key || String(index),
      startTime: (r.meta && r.meta.startDate) || '',
      sessions: [r],
      isNonSessionItem: isNonSessionItem((r.meta && r.meta.sessionType) || ''),
    }));
  }

  const groups = [];
  const map = {};

  let nonSessionCount = 0;
  results.forEach(result => {
    const startTime = (result.meta && result.meta.startDate) || '';
    const sessionType = (result.meta && result.meta.sessionType) || '';

    if (isNonSessionItem(sessionType)) {
      groups.push({
        id: `${startTime}_nonsession_${nonSessionCount++}`,
        startTime,
        sessions: [result],
        isNonSessionItem: true,
      });
    } else {
      if (!map[startTime]) {
        map[startTime] = { id: startTime, startTime, sessions: [], isNonSessionItem: false };
        groups.push(map[startTime]);
      }
      map[startTime].sessions.push(result);
    }
  });

  groups.sort((a, b) => {
    if (!a.startTime) return -1;
    if (!b.startTime) return 1;
    const timeDiff = new Date(a.startTime) - new Date(b.startTime);
    if (timeDiff !== 0) return timeDiff;
    if (a.isNonSessionItem && !b.isNonSessionItem) return -1;
    if (!a.isNonSessionItem && b.isNonSessionItem) return 1;
    return 0;
  });

  const hasSearchTerm = !!(searchTerm || '').trim();
  if (!hasSearchTerm) {
    groups.forEach(group => {
      group.sessions.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    });
  }

  return groups;
}
